import { DataRefreshEvent, type NotificationDto } from "@fp/admin-api-client";
import {
  getGetNotificationCountsQueryKey,
  getListNotificationsQueryKey,
  listNotifications,
  useDeleteAllNotifications,
  useDeleteNotifications,
  useGetNotificationCounts,
  useMarkAllNotificationsRead,
  useMarkAllNotificationsUnread,
  useMarkNotificationRead,
  useMarkNotificationsRead,
  useMarkNotificationsUnread,
} from "@fp/admin-api-client/notifications/notifications.client";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useDataRefresh } from "@/hooks/useSignalR";
import { toast } from "@/lib/toast";

const PAGE_SIZE = 25;

export interface NotificationCounts {
  unreadCount: number;
  totalCount: number;
}

export interface NotificationFeed {
  notifications: NotificationDto[];
  isLoading: boolean;
  isError: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
}

export interface NotificationDeleteState {
  /**
   * `onDeleted` fires only on success, so a failed delete leaves the confirmation
   * open with the toast beside it rather than closing as though it had worked.
   */
  deleteMany: (notificationIds: string[], onDeleted: () => void) => void;
  deleteAll: (unreadOnly: boolean, onDeleted: () => void) => void;
  isDeleting: boolean;
}

export interface NotificationReadState {
  markRead: (notificationId: string) => void;
  markManyRead: (notificationIds: string[]) => void;
  markManyUnread: (notificationIds: string[]) => void;
  markAllRead: () => void;
  markAllUnread: () => void;
  isMarkingMany: boolean;
}

function useRefreshNotifications() {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getListNotificationsQueryKey() });
    queryClient.invalidateQueries({
      queryKey: getGetNotificationCountsQueryKey(),
    });
  }, [queryClient]);
}

export function useNotificationCounts(): NotificationCounts {
  const refresh = useRefreshNotifications();
  const { data } = useGetNotificationCounts();

  useDataRefresh(DataRefreshEvent.NotificationsChanged, refresh);

  return {
    unreadCount: data?.data.unreadCount ?? 0,
    totalCount: data?.data.totalCount ?? 0,
  };
}

export function useNotificationFeed(unreadOnly: boolean): NotificationFeed {
  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: getListNotificationsQueryKey({
      pageSize: PAGE_SIZE,
      unreadOnly,
    }),
    queryFn: ({ pageParam }) =>
      listNotifications({ page: pageParam, pageSize: PAGE_SIZE, unreadOnly }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.data.hasNextPage ? lastPage.data.page + 1 : undefined,
  });

  const notifications = useMemo(
    () => data?.pages.flatMap((page) => page.data.items ?? []) ?? [],
    [data],
  );

  return {
    notifications,
    isLoading,
    isError,
    hasMore: hasNextPage,
    isLoadingMore: isFetchingNextPage,
    loadMore: fetchNextPage,
  };
}

export function useNotificationReadState(): NotificationReadState {
  const refresh = useRefreshNotifications();

  const mutation = {
    onSuccess: () => refresh(),
    onError: () => toast.error("Could not update the notification."),
  };

  const bulkMutation = {
    onSuccess: () => refresh(),
    onError: () => toast.error("Could not update the notifications."),
  };

  const markReadMutation = useMarkNotificationRead({ mutation });
  const markManyReadMutation = useMarkNotificationsRead({
    mutation: bulkMutation,
  });
  const markManyUnreadMutation = useMarkNotificationsUnread({
    mutation: bulkMutation,
  });
  const markAllReadMutation = useMarkAllNotificationsRead({
    mutation: bulkMutation,
  });
  const markAllUnreadMutation = useMarkAllNotificationsUnread({
    mutation: bulkMutation,
  });

  return {
    markRead: (notificationId) => markReadMutation.mutate({ id: notificationId }),
    markManyRead: (notificationIds) =>
      markManyReadMutation.mutate({ data: { notificationIds } }),
    markManyUnread: (notificationIds) =>
      markManyUnreadMutation.mutate({ data: { notificationIds } }),
    markAllRead: () => markAllReadMutation.mutate(),
    markAllUnread: () => markAllUnreadMutation.mutate(),
    isMarkingMany:
      markManyReadMutation.isPending ||
      markManyUnreadMutation.isPending ||
      markAllReadMutation.isPending ||
      markAllUnreadMutation.isPending,
  };
}

export function useNotificationDeleteState(): NotificationDeleteState {
  const refresh = useRefreshNotifications();

  const mutation = {
    onSuccess: () => refresh(),
    onError: () => toast.error("Could not delete the notifications."),
  };

  const deleteManyMutation = useDeleteNotifications({ mutation });
  const deleteAllMutation = useDeleteAllNotifications({ mutation });

  return {
    deleteMany: (notificationIds, onDeleted) =>
      deleteManyMutation.mutate({ data: { notificationIds } }, { onSuccess: onDeleted }),
    deleteAll: (unreadOnly, onDeleted) =>
      deleteAllMutation.mutate({ params: { unreadOnly } }, { onSuccess: onDeleted }),
    isDeleting: deleteManyMutation.isPending || deleteAllMutation.isPending,
  };
}